import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { UserNotificationPreferencesService } from '../../services/userNotificationPreferencesService';
import BasePageLayout from '../../components/BasePageLayout';
import '../../styles/pages/UnsubscribePage.css';

const UnsubscribePage: React.FC = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    error?: string;
  } | null>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');

    if (token) {
      processUnsubscribe(token);
    } else {
      setResult({
        success: false,
        message: 'Invalid unsubscribe link',
        error: 'No token provided'
      });
      setLoading(false);
    }
  }, [location]);

  const processUnsubscribe = async (token: string) => {
    setLoading(true);
    
    try {
      const response = await UserNotificationPreferencesService.processUnsubscribeRequest(token);
      
      setResult({
        success: response.success,
        message: response.message || 'Unsubscribe request processed',
        error: response.error
      });
    } catch (error) {
      console.error('Error processing unsubscribe:', error);
      setResult({
        success: false,
        message: 'Failed to process unsubscribe request',
        error: 'An unexpected error occurred'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasePageLayout>
      <div className="unsubscribe-page">
        <div className="unsubscribe-container">
          {loading ? (
            <div className="loading-section">
              <div className="loading-spinner">
                <i className="bi bi-arrow-clockwise spin"></i>
              </div>
              <h2>Processing your request...</h2>
              <p>Please wait while we update your notification preferences.</p>
            </div>
          ) : (
            <div className="result-section">
              <div className={`result-icon ${result?.success ? 'success' : 'error'}`}>
                <i className={`bi ${result?.success ? 'bi-check-circle' : 'bi-exclamation-triangle'}`}></i>
              </div>
              
              <h2>{result?.success ? 'Successfully Unsubscribed' : 'Unsubscribe Failed'}</h2>
              
              <div className={`result-message ${result?.success ? 'success' : 'error'}`}>
                <p>{result?.message}</p>
                {result?.error && (
                  <p className="error-details">Error: {result.error}</p>
                )}
              </div>

              {result?.success ? (
                <div className="success-actions">
                  <div className="success-info">
                    <h3>What happens next?</h3>
                    <ul>
                      <li>Your notification preferences have been updated</li>
                      <li>You will no longer receive the blocked notifications</li>
                      <li>You can manage all your preferences in your account settings</li>
                      <li>You can resubscribe at any time through your account</li>
                    </ul>
                  </div>
                  
                  <div className="action-buttons">
                    <Link to="/profile/me" className="btn btn-primary">
                      <i className="bi bi-person-gear"></i>
                      Manage All Preferences
                    </Link>
                    <Link to="/" className="btn btn-secondary">
                      <i className="bi bi-house"></i>
                      Return to Home
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="error-actions">
                  <div className="error-info">
                    <h3>What you can try:</h3>
                    <ul>
                      <li>Check if the unsubscribe link is complete and hasn't been truncated</li>
                      <li>Make sure you're using the link from the original notification</li>
                      <li>Contact support if you continue to have issues</li>
                      <li>Manage your preferences directly through your account settings</li>
                    </ul>
                  </div>
                  
                  <div className="action-buttons">
                    <Link to="/profile/me" className="btn btn-primary">
                      <i className="bi bi-person-gear"></i>
                      Manage Preferences
                    </Link>
                    <Link to="/contact" className="btn btn-secondary">
                      <i className="bi bi-envelope"></i>
                      Contact Support
                    </Link>
                  </div>
                </div>
              )}

              <div className="additional-info">
                <h3>About Notification Management</h3>
                <div className="info-grid">
                  <div className="info-card">
                    <i className="bi bi-bell-slash"></i>
                    <h4>Project-Specific</h4>
                    <p>Block notifications from specific projects while keeping others</p>
                  </div>
                  <div className="info-card">
                    <i className="bi bi-person-x"></i>
                    <h4>Publisher-Specific</h4>
                    <p>Block all notifications from a particular project creator</p>
                  </div>
                  <div className="info-card">
                    <i className="bi bi-toggle-off"></i>
                    <h4>Complete Disable</h4>
                    <p>Turn off all project release notifications entirely</p>
                  </div>
                  <div className="info-card">
                    <i className="bi bi-gear"></i>
                    <h4>Fine-Tune</h4>
                    <p>Customize frequency, types, and timing of notifications</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </BasePageLayout>
  );
};

export default UnsubscribePage;
